import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const CATEGORIES = [
  'Challenge 5-6',
  'Challenge 7-8',
  'Challenge 9-10',
  'Challenge 11-12',
  'Challenge 13-14',
  'Youth (15-16)',
  'Junior (17-18)',
  'Under-23',
  'Elite',
]

const CYCLO_COMPS = [
  'Belum Ada',
  'Garmin',
  'Wahoo',
  'iGPSPORT',
  'Bryton',
  'Cateye',
  'Magene',
  'Xoss',
  'Sigma',
  'Lezyne',
  'Hammerhead',
]

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.75rem',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '8px',
  color: '#f1f5f9',
  fontSize: '1rem',
  boxSizing: 'border-box',
  appearance: 'none',
}

const labelStyle = {
  color: '#94a3b8',
  fontSize: '0.75rem',
  marginBottom: '0.25rem',
  display: 'block',
}

const sectionStyle = {
  background: '#1e293b',
  borderRadius: '12px',
  padding: '1rem',
  marginBottom: '1rem',
}

const sectionTitleStyle = {
  color: '#f1f5f9',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  marginBottom: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
}

export default function AthleteProfile({ profile, onBack }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)

  // Profile fields (from `profiles` table)
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')

  // Athlete fields (from `athletes` table)
  const [category, setCategory] = useState('Youth (15-16)')
  const [heightCm, setHeightCm] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [hrRest, setHrRest] = useState('')
  const [cyclingExpYears, setCyclingExpYears] = useState('')
  const [cycloComp, setCycloComp] = useState('Belum Ada')

  // Password fields
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // ── Fetch existing data ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      // Profiles
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', profile.id)
        .single()

      if (profileData) {
        setFullName(profileData.full_name || '')
        setPhone(profileData.phone || '')
      }

      // Athletes
      const { data: athleteData } = await supabase
        .from('athletes')
        .select('category, height_cm, weight_kg, hr_rest, cycling_exp_years, cyclo_comp')
        .eq('profile_id', profile.id)
        .single()

      if (athleteData) {
        setCategory(athleteData.category || 'Youth (15-16)')
        setHeightCm(athleteData.height_cm?.toString() || '')
        setWeightKg(athleteData.weight_kg?.toString() || '')
        setHrRest(athleteData.hr_rest?.toString() || '')
        setCyclingExpYears(athleteData.cycling_exp_years?.toString() || '')
        setCycloComp(athleteData.cyclo_comp || 'Belum Ada')
      }

      setLoading(false)
    }

    fetchData()
  }, [profile.id])

  // ── BMI helper ───────────────────────────────────────────────────────────
  const bmi =
    heightCm && weightKg
      ? (parseFloat(weightKg) / Math.pow(parseFloat(heightCm) / 100, 2)).toFixed(1)
      : null

  const getBmiLabel = (bmi) => {
    if (!bmi) return null
    if (bmi < 18.5) return { label: 'Kurus', color: '#38bdf8' }
    if (bmi < 25)   return { label: 'Normal', color: '#34d399' }
    if (bmi < 30)   return { label: 'Gemuk', color: '#facc15' }
    return            { label: 'Obesitas', color: '#f87171' }
  }

  const bmiInfo = getBmiLabel(parseFloat(bmi))

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null)
    setPasswordError(null)

    // Password validation
    if (newPassword || confirmPassword) {
      if (newPassword.length < 6) {
        setPasswordError('Password minimal 6 karakter')
        return
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('Konfirmasi password tidak cocok')
        return
      }
    }

    setSaving(true)

    try {
      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim(),
          phone: phone.trim() || null,
        })
        .eq('id', profile.id)

      if (profileError) throw profileError

      // Upsert athletes table
      const { error: athleteError } = await supabase
        .from('athletes')
        .upsert(
          {
            profile_id: profile.id,
            category,
            height_cm: heightCm ? parseInt(heightCm) : null,
            weight_kg: weightKg ? parseFloat(weightKg) : null,
            hr_rest: hrRest ? parseInt(hrRest) : null,
            cycling_exp_years: cyclingExpYears ? parseInt(cyclingExpYears) : null,
            cyclo_comp: cycloComp,
          },
          { onConflict: 'profile_id' }
        )

      if (athleteError) throw athleteError

      // Update password if provided
      if (newPassword) {
        const { error: pwError } = await supabase.auth.updateUser({
          password: newPassword,
        })
        if (pwError) throw pwError
      }

      setSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    }

    setSaving(false)
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#38bdf8',
        fontSize: '1rem',
      }}>
        Memuat profil...
      </div>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e',
        gap: '1rem',
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: '1px solid #334155',
            color: '#94a3b8',
            padding: '0.4rem 0.75rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            flexShrink: 0,
          }}
        >
          ← Kembali
        </button>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#38bdf8' }}>
            ⚙️ Profil Saya
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
            {profile?.full_name}
          </div>
        </div>
      </div>

      <div style={{ padding: '1rem 1.5rem', paddingBottom: '6rem' }}>

        {/* ── Seksi 1: Data Diri ── */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>👤 Data Diri</div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Nama Lengkap</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Nama lengkap"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Nomor HP / WhatsApp</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="08xxxxxxxxxx"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Kategori</label>
            <div style={{ position: 'relative' }}>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  ...inputStyle,
                  paddingRight: '2rem',
                  cursor: 'pointer',
                }}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <span style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                pointerEvents: 'none',
                fontSize: '0.75rem',
              }}>▼</span>
            </div>
          </div>
        </div>

        {/* ── Seksi 2: Data Fisik ── */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>📏 Data Fisik</div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
            marginBottom: bmi ? '0.75rem' : 0,
          }}>
            <div>
              <label style={labelStyle}>Tinggi Badan (cm)</label>
              <input
                type="number"
                value={heightCm}
                onChange={e => setHeightCm(e.target.value)}
                placeholder="165"
                min="100"
                max="220"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Berat Badan (kg)</label>
              <input
                type="number"
                value={weightKg}
                onChange={e => setWeightKg(e.target.value)}
                placeholder="55"
                step="0.1"
                min="30"
                max="150"
                style={inputStyle}
              />
            </div>
          </div>

          {/* BMI indicator */}
          {bmi && bmiInfo && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: `${bmiInfo.color}15`,
              border: `1px solid ${bmiInfo.color}44`,
              borderRadius: '8px',
              padding: '0.5rem 0.75rem',
              marginBottom: '0.75rem',
            }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>BMI</span>
              <span style={{ fontSize: '1rem', fontWeight: 'bold', color: bmiInfo.color }}>
                {bmi}
              </span>
              <span style={{
                fontSize: '0.7rem',
                color: bmiInfo.color,
                fontWeight: 'bold',
                background: `${bmiInfo.color}22`,
                padding: '2px 8px',
                borderRadius: '999px',
              }}>
                {bmiInfo.label}
              </span>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
          }}>
            <div>
              <label style={labelStyle}>HR Istirahat (bpm)</label>
              <input
                type="number"
                value={hrRest}
                onChange={e => setHrRest(e.target.value)}
                placeholder="58"
                min="30"
                max="120"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Pengalaman Sepeda (tahun)</label>
              <input
                type="number"
                value={cyclingExpYears}
                onChange={e => setCyclingExpYears(e.target.value)}
                placeholder="2"
                min="0"
                max="30"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* ── Seksi 3: Peralatan ── */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>🔧 Peralatan</div>

          <label style={labelStyle}>Merk Cyclo Comp</label>
          <div style={{ position: 'relative' }}>
            <select
              value={cycloComp}
              onChange={e => setCycloComp(e.target.value)}
              style={{
                ...inputStyle,
                paddingRight: '2rem',
                cursor: 'pointer',
              }}
            >
              {CYCLO_COMPS.map(comp => (
                <option key={comp} value={comp}>{comp}</option>
              ))}
            </select>
            <span style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8',
              pointerEvents: 'none',
              fontSize: '0.75rem',
            }}>▼</span>
          </div>

          {cycloComp !== 'Belum Ada' && (
            <div style={{
              marginTop: '0.5rem',
              background: '#34d39915',
              border: '1px solid #34d39944',
              borderRadius: '6px',
              padding: '0.4rem 0.6rem',
              fontSize: '0.7rem',
              color: '#34d399',
            }}>
              ✅ {cycloComp} — data bisa disync ke Strava (coming soon)
            </div>
          )}
        </div>

        {/* ── Seksi 4: Ganti Password ── */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>🔒 Ganti Password</div>
          <div style={{
            fontSize: '0.72rem',
            color: '#475569',
            marginBottom: '0.75rem',
          }}>
            Kosongkan jika tidak ingin mengubah password.
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Password Baru</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimal 6 karakter"
                style={{ ...inputStyle, paddingRight: '2.5rem' }}
              />
              <button
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  padding: 0,
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Konfirmasi Password Baru</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Ulangi password baru"
                style={{
                  ...inputStyle,
                  paddingRight: '2.5rem',
                  borderColor:
                    confirmPassword && confirmPassword !== newPassword
                      ? '#ef4444'
                      : confirmPassword && confirmPassword === newPassword
                      ? '#34d399'
                      : '#334155',
                }}
              />
              {confirmPassword && (
                <span style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.85rem',
                }}>
                  {confirmPassword === newPassword ? '✅' : '❌'}
                </span>
              )}
            </div>
          </div>

          {passwordError && (
            <div style={{
              marginTop: '0.5rem',
              color: '#f87171',
              fontSize: '0.75rem',
              background: '#f8717115',
              border: '1px solid #f8717144',
              borderRadius: '6px',
              padding: '0.4rem 0.6rem',
            }}>
              ⚠️ {passwordError}
            </div>
          )}

          {/* Password strength indicator */}
          {newPassword.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                {[1, 2, 3, 4].map(i => {
                  const strength =
                    newPassword.length >= 12 ? 4
                    : newPassword.length >= 8 ? 3
                    : newPassword.length >= 6 ? 2
                    : 1
                  return (
                    <div key={i} style={{
                      flex: 1,
                      height: '4px',
                      borderRadius: '999px',
                      background: i <= strength
                        ? strength === 1 ? '#f87171'
                        : strength === 2 ? '#facc15'
                        : strength === 3 ? '#34d399'
                        : '#38bdf8'
                        : '#1e293b',
                      transition: 'background 0.2s',
                    }} />
                  )
                })}
              </div>
              <div style={{ fontSize: '0.65rem', color: '#475569' }}>
                {newPassword.length < 6 ? 'Terlalu pendek'
                  : newPassword.length < 8 ? 'Cukup'
                  : newPassword.length < 12 ? 'Kuat'
                  : 'Sangat kuat'}
              </div>
            </div>
          )}
        </div>

        {/* Error global */}
        {error && (
          <div style={{
            background: '#ef444420',
            border: '1px solid #ef4444',
            color: '#ef4444',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div style={{
            background: '#34d39920',
            border: '1px solid #34d39955',
            color: '#34d399',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            textAlign: 'center',
            fontWeight: 'bold',
          }}>
            ✅ Profil berhasil disimpan!
          </div>
        )}

      </div>

      {/* ── Sticky footer buttons ── */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#0a0f1e',
        borderTop: '1px solid #1e293b',
        padding: '0.75rem 1.5rem',
        display: 'flex',
        gap: '0.75rem',
        zIndex: 50,
      }}>
        <button
          onClick={onBack}
          disabled={saving}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '10px',
            color: '#94a3b8',
            fontSize: '0.9rem',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          Batal
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 2,
            padding: '0.75rem',
            background: saving ? '#334155' : '#84cc16',
            border: 'none',
            borderRadius: '10px',
            color: saving ? '#94a3b8' : '#0f172a',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'background 0.2s',
          }}
        >
          {saving ? (
            <>⏳ Menyimpan...</>
          ) : (
            <>💾 Simpan Perubahan</>
          )}
        </button>
      </div>
    </div>
  )
}